import { useTranslation } from 'react-i18next'
import { xor } from 'lodash'

import { useBulkDisbursementAction } from '../useBulkDisbursementAction'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Modal, Text } from '@percent/lemonade'
import { MarkAsApprovedBulkProps } from './MarkAsApprovedBulk.types'

export function MarkAsApprovedBulk({
  isOpen,
  onClose,
  selectedDisbursementIds,
  refreshData,
  onSelectedDisbursementIdsChange
}: Readonly<MarkAsApprovedBulkProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const { summary, isPending, execute, resetSummary } = useBulkDisbursementAction(
    disbursementsService.bulkApproveDisbursements,
    ids => ids
  )

  const handleClose = () => {
    if (isPending) {
      return
    }

    refreshData?.()
    onSelectedDisbursementIdsChange(xor(selectedDisbursementIds, summary?.succeededDisbursementIds ?? []))
    resetSummary()
    onClose()
  }

  const handleSubmit = async () => {
    if (selectedDisbursementIds.length === 0) {
      return
    }

    await execute(selectedDisbursementIds)
  }

  const failedCount = summary?.failedDisbursementIds.length ?? 0
  const succeededCount = summary?.succeededDisbursementIds.length ?? 0
  const hasSummary = !!summary

  const successModal = hasSummary && succeededCount > 0 && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.bulkDisbursementsMarkAsApproved.result.success.title', { count: succeededCount })}
      description={
        succeededCount !== selectedDisbursementIds.length
          ? t('dialog.bulkDisbursementsMarkAsApproved.result.success.description', {
              count: failedCount,
              allCount: selectedDisbursementIds.length
            })
          : undefined
      }
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-mark-as-approved-success-modal"
      buttonTestId="bulk-disbursements-mark-as-approved-success-close-button"
    />
  )

  const errorModal = hasSummary && succeededCount === 0 && failedCount > 0 && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.bulkDisbursementsMarkAsApproved.result.error.description', { count: failedCount })}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-mark-as-approved-error-modal"
      buttonTestId="bulk-disbursements-mark-as-approved-error-close-button"
    />
  )

  const modalContent = successModal || errorModal || (
    <ActionModal
      title={t('dialog.bulkDisbursementsMarkAsApproved.title')}
      primaryButtonText={t('button.confirm')}
      secondaryButtonText={t('button.cancel')}
      type="submit"
      loading={isPending}
      disabled={isPending || selectedDisbursementIds.length === 0}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      primaryBtnTestId="bulk-disbursements-mark-as-approved-submit-button"
      secondaryBtnTestId="bulk-disbursements-mark-as-approved-cancel-button"
    >
      <Text size="small">
        {t('dialog.bulkDisbursementsMarkAsApproved.description', { count: selectedDisbursementIds.length })}
      </Text>
    </ActionModal>
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {modalContent}
    </Modal>
  )
}
